import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  DefaultSort,
  getNextSortDirection,
  SortDirection,
  SortHelper,
  SortService,
  sortData,
  TableColumn,
} from '@app/shared';
import { FilterChangeEvent } from '../../features/maestros/components/maestro-filter/maestro-filter.component';
import { NzDrawerService } from 'ng-zorro-antd/drawer';
import { MaestrosFormDrawerComponent } from '../../features/maestros/drawers/maestros-form-drawer/maestros-form-drawer.component';
import { combineLatest, take, Subscription } from 'rxjs';
import { MaestrosUtilsService } from '../../features/maestros/services/maestros-utils.service';
import { NzModalService } from 'ng-zorro-antd/modal';
import { MaestrosDetailsDrawerComponent } from 'src/app/features/maestros/drawers/maestros-details-drawer/maestros-details-drawer.component';
import { FlowComponent } from '../../features/maestros/pages/flow/flow.component';
import { OrdersDetailsActivitiesDrawerComponent } from '../../features/project/shared/drawers/orders-details-activities-drawer/orders-details-activities-drawer.component';
import {
  deepGet,
  getPartnerDisplayName,
  getSafeImageUrl,
  isExpirationAlert,
  NavigationService,
  ScreenService,
} from '@app/core';
import { GlobalValuesService } from '../../core/services/global-values.service';
import {
  ConfirmationModalComponent,
  ConfirmationModalConfig,
} from '@app/shared';

export interface PermissionsMaestros {
  create: boolean;
  edit: boolean;
  delete: boolean;
  viewDetails: boolean;
}

interface GroupValueOption {
  key: string;
  label: string;
  value: string | number | boolean | null;
  count: number;
  isActive: boolean;
}

@Component({
  selector: 'app-layout-maestro',
  standalone: false,

  templateUrl: './layout-maestro.component.html',
  styleUrl: './layout-maestro.component.less',
})
export class LayoutMaestroComponent implements OnInit, OnDestroy {
  @Input() maestroName: string = 'Sin nombre';
  @Input() columnsData: TableColumn[] = [];
  @Input() formColumnsData?: TableColumn[];
  @Input() tableColumnsData?: TableColumn[];
  columnsDataToShow: TableColumn[] = [];
  @Input() showDetails?: boolean = true;
  @Input() hideHeader?: boolean = false;
  @Input() permissions: PermissionsMaestros = {
    create: true,
    edit: true,
    delete: true,
    viewDetails: true,
  };
  @Input() secondaryBtnText: string | undefined;
  @Input() createButtonText: string = 'Añadir';
  @Input() showPageSizeSelector: boolean = false;
  private readonly allowedPageSizeOptions: number[] = [10, 25, 50, 100];
  private _pageSizeOptions: number[] = [...this.allowedPageSizeOptions];
  @Input()
  set pageSizeOptions(_: number[] | undefined) {
    this._pageSizeOptions = [...this.allowedPageSizeOptions];
  }
  get pageSizeOptions(): number[] {
    return this._pageSizeOptions;
  }
  @Input() showCheckboxes: boolean = false;
  @Input() customDrawer?: {
    formComponent?: any;
    formComponentWidth?: number;
    detailsComponent?: any;
    detailsComponentWidth?: string | number;
  };
  @Input() isFlow: boolean = false;
  @Input() enableFilters: boolean = false;
  @Input() filterableColumns?: string[];
  @Input() enableGrouping: boolean = false;
  @Input() groupableColumns?: string[];
  // Opcional: orden con el que abre la tabla. Sin él, se muestra en el orden en que llegan los datos.
  @Input() defaultSort?: DefaultSort;
  @Output() createEvent: EventEmitter<any> = new EventEmitter<any>();
  @Output() updateEvent: EventEmitter<any> = new EventEmitter<any>();
  @Output() deleteEvent: EventEmitter<number> = new EventEmitter<number>();
  @Output() deleteMultipleEvent: EventEmitter<number[]> = new EventEmitter<
    number[]
  >();
  @Output() secondaryBtnClick: EventEmitter<void> = new EventEmitter<void>();

  @ViewChild('confirmationModal')
  confirmationModal!: ConfirmationModalComponent;
  private pendingDeleteId?: number;
  private drawerSubscriptions: Subscription[] = [];
  formDrawerRef: any;
  detailsDrawerRef: any;
  groupDrawerRef: any;
  searchValue: string = '';
  filteredData: any[] = [];
  renderData: any[] = [];
  // Columna por la que se ordena actualmente. La direccion vive en
  // activeSortColumn.sortDirection, para no duplicar el estado.
  private activeSortColumn?: TableColumn;
  viewImageModalRef: any;
  @ViewChild('viewImageModalTmpl') viewImageModalTmpl: any;
  @ViewChild('groupDrawerTmpl') groupDrawerTmpl!: TemplateRef<any>;
  tmpImage: string = '';
  pageSize: number = 10;
  pageIndex: number = 1;

  groupableColumnsList: TableColumn[] = [];
  shownGroupableColumns: TableColumn[] = [];
  selectedGroupColumn?: TableColumn;
  selectedGroupValueOptions: GroupValueOption[] = [];
  shownGroupValueOptions: GroupValueOption[] = [];
  activeGroupColumns: TableColumn[] = [];
  draftGroupColumns: TableColumn[] = [];
  searchGroupValue: string = '';
  searchGroupItemValue: string = '';
  allGroupValuesSelected = false;
  groupValuesIndeterminate = false;
  private activeGroupSelections = new Map<string, Set<string>>();
  private draftGroupSelections = new Map<string, Set<string>>();
  private initializedGroupKeys = new Set<string>();
  private expandedGroupKeys = new Set<string>();

  getSafeImageUrl = getSafeImageUrl;

  pageSizeChange(size: number): void {
    this.pageSize = this.normalizePageSize(size);
    this.clampPageIndex();
  }

  pageIndexChange(index: number): void {
    this.pageIndex = index;
  }

  /**
   * Ajusta la pagina actual cuando deja de existir, por ejemplo al eliminar la
   * ultima fila de la ultima pagina. Se calcula sobre renderData porque es lo
   * que pagina el nz-table.
   */
  private clampPageIndex(): void {
    const maxPage = Math.max(
      1,
      Math.ceil(this.renderData.length / this.pageSize)
    );
    if (this.pageIndex > maxPage) {
      this.pageIndex = maxPage;
    }
  }

  private normalizePageSize(size: number): number {
    const parsed = Math.floor(Number(size));
    if (!Number.isFinite(parsed)) return 10;
    if (parsed >= 100) return 100;
    if (parsed > 50) return 100;
    if (parsed > 25) return 50;
    if (parsed > 10) return 25;
    return 10;
  }

  /**
   * Obtiene las iniciales de un nombre y apellido (o del item completo si no se pasan parámetros)
   * @param nameOrItem - Nombre del partner o el item completo
   * @param surName - Apellido del partner
   * @returns Las iniciales en mayúsculas (ej: "Juan Pérez" -> "JP")
   */
  getInitials(nameOrItem?: string | any, surName?: string): string {
    // Si el primer parámetro es un objeto (item completo)
    if (typeof nameOrItem === 'object' && nameOrItem !== null) {
      const item = nameOrItem;

      // Intentar obtener nombre y apellido directamente del item
      let name = item.name;
      let surname = item.surName;

      // Si no tiene nombre/apellido directo, buscar en partner
      if ((!name || !surname) && item.partner) {
        name = name || item.partner.name;
        surname = surname || item.partner.surName;
      }

      // Generar iniciales
      let initials = '';
      if (name && name.trim()) {
        initials += name.trim().charAt(0).toUpperCase();
      }
      if (surname && surname.trim()) {
        initials += surname.trim().charAt(0).toUpperCase();
      }

      return initials || '?';
    }

    // Si son parámetros string (modo legacy)
    const name = nameOrItem as string;
    if (!name && !surName) {
      return '?';
    }

    let initials = '';

    if (name && name.trim()) {
      initials += name.trim().charAt(0).toUpperCase();
    }

    if (surName && surName.trim()) {
      initials += surName.trim().charAt(0).toUpperCase();
    }

    return initials || '?';
  }

  /**
   * Obtiene una propiedad anidada de un objeto
   * @param item - Objeto principal
   * @param path - Ruta de la propiedad (ej: 'partnerId')
   * @param nestedProp - Propiedad anidada (ej: 'avatar')
   * @returns El valor de la propiedad anidada o undefined
   */
  getNestedProperty(item: any, path: string, nestedProp: string): any {
    if (!item || !path) return undefined;

    // Caso especial: si el path es managerId, buscar en manager.partner
    if (path === 'managerId' && nestedProp === 'avatar') {
      const manager = (item as any).manager;
      if (manager?.partner) {
        return manager.partner.avatar;
      }
    }

    // Si el path termina con 'Id', obtener el objeto relacionado
    // Ejemplo: partnerId -> partner
    let objectKey = path;
    if (path.endsWith('Id')) {
      objectKey = path.slice(0, -2); // Quitar 'Id' del final
    }

    const nestedObject = item[objectKey];
    if (!nestedObject) return undefined;

    return nestedObject[nestedProp];
  }

  /**
   * Obtiene las iniciales desde un objeto anidado
   * @param item - Objeto principal
   * @param path - Ruta de la propiedad (ej: 'partnerId')
   * @returns Las iniciales en mayúsculas
   */
  getInitialsFromNested(item: any, path: string): string {
    if (!item || !path) return '?';

    // Caso especial: si el path es managerId, obtener iniciales del partner
    if (path === 'managerId') {
      const manager = (item as any).manager;
      if (manager?.partner) {
        const name = manager.partner.name || '';
        const surName = manager.partner.surName || '';
        return this.getInitials(name, surName);
      }
      return '?';
    }

    // Si el path termina con 'Id', obtener el objeto relacionado
    let objectKey = path;
    if (path.endsWith('Id')) {
      objectKey = path.slice(0, -2);
    }

    const nestedObject = item[objectKey];
    if (!nestedObject) return '?';

    const name = nestedObject.name || '';
    const surName = nestedObject.surName || '';
    return this.getInitials(name, surName);
  }

  /**
   * Obtiene un display name seguro desde una propiedad anidada
   */
  getNestedDisplayName(item: any, path: string, nestedPath?: string): string {
    if (!item || !path) return '-';

    // Caso especial: si nestedPath es 'managerName', buscar en manager.partner.name
    if (nestedPath === 'managerName' && path === 'managerId') {
      const manager = (item as any).manager;
      if (manager?.partner?.name) {
        return this.normalizeDisplayValue(manager.partner.name);
      }
      return '-';
    }

    // Si el path termina con 'Id', intentar tomar el objeto (ej: partnerId -> partner)
    let objectKey = path.endsWith('Id') ? path.slice(0, -2) : path;
    const nestedObject = item[objectKey];

    if (!nestedObject) {
      // Si no hay objeto, intentar deepGet directo
      const val = nestedPath
        ? this.deepGet(item, `${path}.${nestedPath}`)
        : this.deepGet(item, path);
      return this.normalizeDisplayValue(val);
    }

    // Si hay nestedPath, usarlo
    if (nestedPath && nestedObject[nestedPath] !== undefined) {
      return this.normalizeDisplayValue(nestedObject[nestedPath]);
    }

    // Caso común: usar name + surName si existen
    if (nestedObject.name || nestedObject.surName) {
      const name = nestedObject.name || '';
      const surName = nestedObject.surName || '';
      const full = `${name} ${surName}`.trim();
      return full || '-';
    }

    return this.normalizeDisplayValue(nestedObject);
  }

  /**
   * Normaliza cualquier valor a string de visualización
   */
  private normalizeDisplayValue(val: any): string {
    if (val === null || val === undefined) return '-';
    if (
      typeof val === 'string' ||
      typeof val === 'number' ||
      typeof val === 'boolean'
    ) {
      return val.toString();
    }
    // Si es un objeto, evitar [object Object]
    if (val.name || val.surName) {
      return getPartnerDisplayName(val) || '-';
    }
    return '-';
  }

  showSearch: boolean = true;
  showGlobalSearch: boolean = false;

  allSelected = false;
  indeterminate = false;
  selectedIds: number[] = [];

  // Propiedades para filtrado
  filterableColumnsList: TableColumn[] = [];
  activeFilters: FilterChangeEvent['filters'] = [];

  constructor(
    private drawerService: NzDrawerService,
    public ms: MaestrosUtilsService,
    public gs: GlobalValuesService,
    public screenService: ScreenService,
    private modalService: NzModalService,
    public navigationService: NavigationService,
    private sortService: SortService
  ) {}

  private _tableData: any[] = [];

  get tableData() {
    return this._tableData;
  }

  @Input()
  set tableData(value: any[]) {
    // Invertir el array para que los nuevos elementos aparezcan al principio
    const nextTableData = value && Array.isArray(value) ? [...value].reverse() : [];

    if (this.hasSameRowReferences(this._tableData, nextTableData)) {
      return;
    }

    this._tableData = nextTableData;
    this.filter();
  }

  private hasSameRowReferences(currentRows: any[], nextRows: any[]): boolean {
    return (
      currentRows.length === nextRows.length &&
      currentRows.every((row, index) => row === nextRows[index])
    );
  }

  ngOnInit(): void {
    const columnsForTable = this.tableColumnsData || this.columnsData;
    this.columnsDataToShow = columnsForTable.filter(
      (column) =>
        !column.noVisibleInTable && !(this.showCheckboxes && column.checkbox)
    );
    this.configureSortFunctions();
    this.applyDefaultSort();

    // Configurar filtros si están habilitados
    if (this.enableFilters) {
      this.setupFilterableColumns();
    }

    if (this.enableGrouping) {
      this.setupGroupableColumns();
    }

    // Watcher para cerrar el modal cuando el loading termine
    this.ms.loading$.subscribe((loading) => {
      if (!loading && this.confirmationModal?.config?.processing) {
        this.confirmationModal.config.processing = false;
        this.confirmationModal.forceClose();
      }
    });
  }

  toggleSearch(event: Event) {
    event.stopPropagation();
    event.preventDefault();
    this.showGlobalSearch = false;
    this.showSearch = !this.showSearch;
  }

  toggleGlobalSearch(event: Event) {
    event.stopPropagation();
    event.preventDefault();
    this.showSearch = false;
    this.showGlobalSearch = !this.showGlobalSearch;
  }

  openDeleteConfirmation(elementId: number): void {
    this.pendingDeleteId = elementId;
    const config: ConfirmationModalConfig = {
      title: 'Eliminar elemento',
      message: '¿Estás seguro que deseas eliminar este elemento?',
      confirmText: 'Eliminar',
      cancelText: 'Cancelar',
      confirmButtonType: 'primary',
      confirmButtonDanger: true,
    };
    this.confirmationModal.config = config;
    this.confirmationModal.open();
  }

  openDeleteMultipleConfirmation(): void {
    this.pendingDeleteId = undefined;
    const config: ConfirmationModalConfig = {
      title: 'Eliminar elementos',
      message: `¿Estás seguro que deseas eliminar ${this.selectedIds.length} elemento(s)?`,
      confirmText: 'Eliminar',
      cancelText: 'Cancelar',
      confirmButtonType: 'primary',
      confirmButtonDanger: true,
    };
    this.confirmationModal.config = config;
    this.confirmationModal.open();
  }

  onDeleteConfirmed(data: { textValue?: string; dateValue?: Date }): void {
    this.ms.loading = true;
    // Actualizar el estado del modal para mostrar loading
    this.confirmationModal.config.processing = true;

    if (this.pendingDeleteId) {
      this.deleteEvent.emit(this.pendingDeleteId);
      // Cerrar el modal inmediatamente después de confirmar
      setTimeout(() => {
        if (this.confirmationModal?.config?.processing) {
          this.confirmationModal.config.processing = false;
          // Cerrar el modal después de un breve delay para que se vea el loading
          setTimeout(() => {
            this.confirmationModal.forceClose();
          }, 200);
        }
      }, 500); // Medio segundo para mostrar el loading brevemente
    } else if (this.selectedIds.length > 0) {
      this.deleteMultipleEvent.emit([...this.selectedIds]);
      this.selectedIds = [];
      this.updateSelectionState();
      // Cerrar el modal inmediatamente después de confirmar
      setTimeout(() => {
        if (this.confirmationModal?.config?.processing) {
          this.confirmationModal.config.processing = false;
          // Cerrar el modal después de un breve delay para que se vea el loading
          setTimeout(() => {
            this.confirmationModal.forceClose();
          }, 200);
        }
      }, 500);
    }
  }

  closeAllDrawers(): void {
    // Cerrar todos los drawers disponibles
    this.detailsDrawerRef?.close();
    this.formDrawerRef?.close();
    this.groupDrawerRef?.close();
  }

  onCreationCompleted(): void {
    // Después de crear exitosamente, cerrar todos los drawers
    this.closeAllDrawers();
  }

  onDeletionCompleted(): void {
    // Después de eliminar exitosamente, cerrar todos los drawers
    this.closeAllDrawers();
  }

  deleteElement(elementId: number): void {
    this.deleteEvent.emit(elementId);
  }

  openFormDrawer(editingItem?: any): void {
    if (this.isFlow) {
      combineLatest({
        isMobile: this.screenService.isMobile$,
        isMedium: this.screenService.isMedium$,
      })
        .pipe(take(1))
        .subscribe(({ isMobile, isMedium }) => {
          this.formDrawerRef = this.drawerService.create({
            nzWidth: 793,
            nzHeight: '98%',
            nzPlacement: isMobile ? 'bottom' : 'right',
            nzClosable: false,
            nzMaskClosable: false,
            nzMask: true,
            nzContent: FlowComponent,
            nzData: {
              flowId: editingItem?.id || undefined,
              readonly: false,
            },
          });
        });
    } else {
      combineLatest({
        isMobile: this.screenService.isMobile$,
        isMedium: this.screenService.isMedium$,
      })
        .pipe(take(1))
        .subscribe(({ isMobile, isMedium }) => {
          this.formDrawerRef = this.drawerService.create({
            nzClosable: false,
            nzMask: true,
            nzWidth: this.customDrawer?.formComponentWidth || '635px',
            nzHeight: '98%',
            nzPlacement: isMobile ? 'bottom' : 'right',
            nzContent:
              this.customDrawer?.formComponent || MaestrosFormDrawerComponent,
            nzData: {
              columnsData: this.formColumnsData || this.columnsData,
              editingElement: editingItem,
              maestroName: this.maestroName,
            },
          });
        });
    }

    this.formDrawerRef.afterOpen.pipe(take(1)).subscribe(() => {
      this.formDrawerRef
        .getContentComponent()
        .createEvent.pipe(take(1))
        .subscribe((value: any) => this.createEvent.emit(value));
      this.formDrawerRef
        .getContentComponent()
        .updateEvent.subscribe((value: any) => this.updateEvent.emit(value));

      this.ms.drawerRef = this.formDrawerRef;
    });

    this.formDrawerRef.afterClose.pipe(take(1)).subscribe(() => {
      this.ms.drawerRef = undefined;
      this.formDrawerRef = null;
    });
  }

  /**
   * Configura las columnas que pueden ser filtradas
   */
  setupFilterableColumns(): void {
    const columnsForTable = this.tableColumnsData || this.columnsData;

    if (this.filterableColumns && this.filterableColumns.length > 0) {
      const filtered = columnsForTable.filter(
        (column) => this.filterableColumns!.includes(column.name)
      );
      
      // Si hay duplicados por nombre, preferir las que tienen type: 'select' y optionList
      const uniqueMap = new Map<string, TableColumn>();
      filtered.forEach(column => {
        const existing = uniqueMap.get(column.name);
        if (!existing) {
          uniqueMap.set(column.name, column);
        } else {
          // Si la nueva columna es select con optionList, y la existente no, reemplazar
          const newIsSelect = column.pathData?.type === 'select' && column.optionList && column.optionList.length > 0;
          const existingIsSelect = existing.pathData?.type === 'select' && existing.optionList && existing.optionList.length > 0;
          if (newIsSelect && !existingIsSelect) {
            uniqueMap.set(column.name, column);
          }
        }
      });
      
      this.filterableColumnsList = Array.from(uniqueMap.values());
    } else {
      this.filterableColumnsList = columnsForTable.filter(
        (column) => column.filterable && !column.noVisibleInTable
      );
    }
  }

  setupGroupableColumns(): void {
    const columnsForTable = this.tableColumnsData || this.columnsData;
    const visibleColumns = columnsForTable.filter(
      (column) =>
        !column.noVisibleInTable &&
        !column.noVisibleInGrouping &&
        !column.checkbox &&
        !!column.pathData &&
        column.pathData.type !== 'image' &&
        column.pathData.type !== 'multi-select'
    );

    const sourceColumns = this.groupableColumns?.length
      ? visibleColumns.filter((column) => this.groupableColumns!.includes(column.name))
      : visibleColumns;

    const uniqueMap = new Map<string, TableColumn>();
    sourceColumns.forEach((column) => {
      if (!uniqueMap.has(column.name)) {
        uniqueMap.set(column.name, column);
      }
    });

    this.groupableColumnsList = Array.from(uniqueMap.values());
    this.shownGroupableColumns = [...this.groupableColumnsList];
    this.selectedGroupColumn = this.shownGroupableColumns[0];
    this.loadGroupValueOptions(this.selectedGroupColumn);
  }

  openGroupDrawerRef(): void {
    if (!this.enableGrouping || !this.groupDrawerTmpl) return;

    this.groupDrawerRef?.close();
    this.draftGroupColumns = [...this.activeGroupColumns];
    this.draftGroupSelections = this.cloneGroupSelections(this.activeGroupSelections);
    this.searchGroupValue = '';
    this.searchGroupItemValue = '';
    this.filterGroupColumns();
    this.selectedGroupColumn = this.getFirstSelectableGroupColumn();
    this.loadGroupValueOptions(this.selectedGroupColumn);

    combineLatest({
      isMobile: this.screenService.isMobile$,
      isMedium: this.screenService.isMedium$,
    })
      .pipe(take(1))
      .subscribe(({ isMobile }) => {
        this.groupDrawerRef = this.drawerService.create({
          nzWidth: 776,
          nzHeight: '98%',
          nzPlacement: isMobile ? 'bottom' : 'right',
          nzClosable: false,
          nzMask: true,
          nzContent: this.groupDrawerTmpl,
        });
      });
  }

  filterGroupColumns(): void {
    const searchValue = this.normalizeText(this.searchGroupValue);

    this.shownGroupableColumns = searchValue
      ? this.groupableColumnsList.filter((column) =>
          this.normalizeText(column.name).includes(searchValue)
        )
      : [...this.groupableColumnsList];

    if (
      this.selectedGroupColumn &&
      !this.shownGroupableColumns.some((column) => column.name === this.selectedGroupColumn!.name)
    ) {
      this.selectedGroupColumn = this.shownGroupableColumns[0];
      this.loadGroupValueOptions(this.selectedGroupColumn);
    }
  }

  cleanSearchGroupValue(): void {
    this.searchGroupValue = '';
    this.filterGroupColumns();
  }

  selectGroupColumn(column: TableColumn): void {
    this.selectedGroupColumn = column;
    this.searchGroupItemValue = '';
    this.loadGroupValueOptions(column);
  }

  filterGroupItems(): void {
    const searchValue = this.normalizeText(this.searchGroupItemValue);

    this.shownGroupValueOptions = searchValue
      ? this.selectedGroupValueOptions.filter((option) =>
          this.normalizeText(option.label).includes(searchValue)
        )
      : [...this.selectedGroupValueOptions];

    this.updateGroupValuesSelectionState();
  }

  cleanSearchGroupItemValue(): void {
    this.searchGroupItemValue = '';
    this.filterGroupItems();
  }

  isDraftGroupColumnActive(column: TableColumn): boolean {
    return (this.draftGroupSelections.get(column.name)?.size || 0) > 0;
  }

  toggleGroupValue(option: GroupValueOption, checked?: boolean): void {
    if (!this.selectedGroupColumn) return;

    const shouldActivate = checked ?? !option.isActive;
    const columnName = this.selectedGroupColumn.name;
    const selectedValues = this.getDraftSelectionSet(columnName);

    option.isActive = shouldActivate;

    if (shouldActivate) {
      selectedValues.add(option.key);
    } else {
      selectedValues.delete(option.key);
    }

    if (selectedValues.size === 0) {
      this.draftGroupSelections.delete(columnName);
      this.draftGroupColumns = this.draftGroupColumns.filter(
        (activeColumn) => activeColumn.name !== columnName
      );
    } else if (!this.draftGroupColumns.some((column) => column.name === columnName)) {
      this.draftGroupColumns = [...this.draftGroupColumns, this.selectedGroupColumn];
    }

    this.selectedGroupValueOptions = this.selectedGroupValueOptions.map((item) =>
      item.key === option.key ? { ...item, isActive: shouldActivate } : item
    );
    this.filterGroupItems();
    this.updateGroupValuesSelectionState();
  }

  getDraftGroupOrder(column: TableColumn): number | null {
    const index = this.draftGroupColumns.findIndex(
      (activeColumn) => activeColumn.name === column.name
    );

    return index >= 0 ? index + 1 : null;
  }

  cleanSelectedGroupColumn(column?: TableColumn): void {
    const columnToClean = column || this.selectedGroupColumn;
    if (!columnToClean) return;

    this.draftGroupSelections.delete(columnToClean.name);
    this.draftGroupColumns = this.draftGroupColumns.filter(
      (activeColumn) => activeColumn.name !== columnToClean.name
    );

    if (this.selectedGroupColumn?.name === columnToClean.name) {
      this.loadGroupValueOptions(this.selectedGroupColumn);
    }
  }

  cleanAllGroups(): void {
    this.draftGroupColumns = [];
    this.draftGroupSelections.clear();
    this.loadGroupValueOptions(this.selectedGroupColumn);
  }

  discardGrouping(): void {
    this.activeGroupColumns = [];
    this.draftGroupColumns = [];
    this.activeGroupSelections.clear();
    this.draftGroupSelections.clear();
    this.initializedGroupKeys.clear();
    this.expandedGroupKeys.clear();
    this.refreshRenderedData();
    this.loadGroupValueOptions(this.selectedGroupColumn);
    this.groupDrawerRef?.close();
  }

  applyGrouping(): void {
    this.activeGroupColumns = [...this.draftGroupColumns];
    this.activeGroupSelections = this.cloneGroupSelections(this.draftGroupSelections);
    this.initializedGroupKeys.clear();
    this.expandedGroupKeys.clear();
    this.refreshRenderedData();
    this.groupDrawerRef?.close();
  }

  private getFirstSelectableGroupColumn(): TableColumn | undefined {
    if (
      this.selectedGroupColumn &&
      this.shownGroupableColumns.some((column) => column.name === this.selectedGroupColumn!.name)
    ) {
      return this.selectedGroupColumn;
    }

    return this.shownGroupableColumns[0] || this.groupableColumnsList[0];
  }

  private loadGroupValueOptions(column?: TableColumn): void {
    if (!column) {
      this.selectedGroupValueOptions = [];
      this.shownGroupValueOptions = [];
      return;
    }

    const selectedValues = this.draftGroupSelections.get(column.name) || new Set<string>();
    const valuesMap = new Map<string, GroupValueOption>();

    if (this.isBooleanGroupColumn(column)) {
      this.addGroupValueOption(valuesMap, selectedValues, 'Sí', true, 0);
      this.addGroupValueOption(valuesMap, selectedValues, 'No', false, 0);
    }

    if (this.shouldLoadGroupOptionsFromOptionList(column)) {
      column.optionList!.forEach((option) => {
        const label = this.getOptionGroupDisplayValue(option, column);
        const value = this.getOptionGroupFilterValue(option);
        const key = this.getGroupValueKey(value, label);

        valuesMap.set(key, {
          key,
          label,
          value,
          count: 0,
          isActive: selectedValues.has(key),
        });
      });
    }

    this.tableData.forEach((row) => {
      const label = this.getGroupColumnDisplayValue(row, column) || 'Sin valor';
      const value = this.getGroupColumnFilterValue(row, column);

      if (this.shouldIgnoreEmptyOptionListGroupValue(column, value, label)) {
        return;
      }

      const key = this.getGroupValueKey(value, label);
      const current = valuesMap.get(key);

      if (current) {
        current.count += 1;
      } else {
        valuesMap.set(key, {
          key,
          label,
          value,
          count: 1,
          isActive: selectedValues.has(key),
        });
      }
    });

    const groupValueOptions = Array.from(valuesMap.values());

    this.selectedGroupValueOptions = this.isBooleanGroupColumn(column)
      ? groupValueOptions.sort((a, b) => Number(b.value === true) - Number(a.value === true))
      : groupValueOptions.sort((a, b) =>
        a.label.localeCompare(b.label, 'es', { sensitivity: 'base' })
      );
    this.filterGroupItems();
  }

  private addGroupValueOption(
    valuesMap: Map<string, GroupValueOption>,
    selectedValues: Set<string>,
    label: string,
    value: string | number | boolean | null,
    count: number
  ): void {
    const key = this.getGroupValueKey(value, label);

    if (!valuesMap.has(key)) {
      valuesMap.set(key, {
        key,
        label,
        value,
        count,
        isActive: selectedValues.has(key),
      });
    }
  }

  private isBooleanGroupColumn(column: TableColumn): boolean {
    const type = column.pathData?.type;
    return type === 'boolean' || type === 'checkboxOnTable' || !!column.checkboxOnTable;
  }

  private shouldIgnoreEmptyOptionListGroupValue(
    column: TableColumn,
    value: string | number | boolean | null,
    label: string
  ): boolean {
    return (
      this.shouldLoadGroupOptionsFromOptionList(column) &&
      (value === null || value === '' || label === '-' || label === 'Sin valor')
    );
  }

  private shouldLoadGroupOptionsFromOptionList(column: TableColumn): boolean {
    return (
      !!column.groupOptionsFromOptionList &&
      !!column.optionList?.length &&
      (column.pathData?.type === 'select' || column.pathData?.type === 'avatar-select')
    );
  }

  private getOptionGroupDisplayValue(option: any, column: TableColumn): string {
    const nestedValue = column.pathData?.nestedPath
      ? this.getPropertyValue(option, column.pathData.nestedPath)
      : undefined;
    const fullName = [option?.name, option?.surName].filter(Boolean).join(' ').trim();

    return String(
      nestedValue ||
      fullName ||
      option?.label ||
      option?.role ||
      option?.value ||
      'Sin valor'
    );
  }

  private getOptionGroupFilterValue(option: any): string | number | boolean | null {
    const value = option?.id ?? option?.value;

    if (value === undefined || value === null || value === '') {
      return null;
    }

    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      return value;
    }

    return String(value);
  }

  private getDraftSelectionSet(columnName: string): Set<string> {
    let selectedValues = this.draftGroupSelections.get(columnName);

    if (!selectedValues) {
      selectedValues = new Set<string>();
      this.draftGroupSelections.set(columnName, selectedValues);
    }

    return selectedValues;
  }

  private cloneGroupSelections(source: Map<string, Set<string>>): Map<string, Set<string>> {
    const clone = new Map<string, Set<string>>();
    source.forEach((values, columnName) => clone.set(columnName, new Set(values)));
    return clone;
  }

  private getGroupValueKey(value: unknown, fallbackLabel: string = 'Sin valor'): string {
    const normalizedValue =
      value === null || value === undefined || value === '' ? fallbackLabel : value;

    if (normalizedValue instanceof Date) {
      return normalizedValue.toISOString();
    }

    if (typeof normalizedValue === 'object') {
      try {
        return JSON.stringify(normalizedValue);
      } catch {
        return String(fallbackLabel || 'Sin valor');
      }
    }

    return String(normalizedValue || 'Sin valor');
  }

  /**
   * Maneja el cambio de filtro desde el componente hijo
   */
  onFilterChange(event: FilterChangeEvent): void {
    this.activeFilters = event.filters;
    this.filter();
  }

  /**
   * Maneja la limpieza del filtro
   */
  onFilterClear(): void {
    this.activeFilters = [];
    this.filter();
  }

  /**
   * Aplica el filtro según el tipo de campo
   */
  private applyFieldFilter(item: any, column: TableColumn, filterValue: any): boolean {
    if (filterValue === null || filterValue === undefined ||
        (filterValue === '' && column.pathData?.type !== 'boolean')) {
      return true;
    }

    const pathData = column.pathData;
    if (!pathData) return true;

    let itemValue: any;

    // Obtener el valor del item según el tipo de columna
    if (pathData.type === 'select' || pathData.type === 'avatar-select') {
      const currentObj = (column.optionList || []).find(
        (opt) => opt.id === item[pathData.name]
      );
      itemValue = this.getPropertyValue(currentObj, pathData.nestedPath || 'name');
    } else if (pathData.type === 'multi-select') {
      // Para multi-select, itemValue es el array de objetos
      itemValue = item[pathData.name];
    } else if (pathData.nestedPath) {
      const nestedObj = item[pathData.name];
      itemValue = this.getPropertyValue(nestedObj, pathData.nestedPath);
    } else {
      itemValue = item[pathData.name];
    }

    // Aplicar filtro según el tipo
    switch (pathData.type) {
      case 'text':
        return itemValue?.toString().toLowerCase().includes(
          filterValue?.toString().toLowerCase()
        );

      case 'number':
        return itemValue?.toString().includes(
          filterValue?.toString()
        );

      case 'date':
        if (!filterValue) return true;
        const filterDate = new Date(filterValue);
        const itemDate = new Date(itemValue);
        return filterDate.toDateString() === itemDate.toDateString();

      case 'select':
      case 'avatar-select':
        return item[pathData.name] === filterValue ||
               itemValue?.toString().toLowerCase() ===
               filterValue?.toString().toLowerCase();

      case 'multi-select':
        // filterValue es un array de IDs seleccionados
        if (!Array.isArray(filterValue) || filterValue.length === 0) {
          return true;
        }
        if (!Array.isArray(itemValue) || itemValue.length === 0) {
          return false;
        }
        return itemValue.some((company: any) => {
          const companyId = company?.id || company;
          return filterValue.includes(companyId);
        });

      case 'boolean':
        return itemValue === filterValue;

      default:
        return true;
    }
  }

  get table(): { data: any[] } {
    return { data: this.renderData };
  }

  filter(): void {
    let tempFilteredData = this.tableData;

    // Obtener todas las propiedades de tipo texto y que se muestren en la tabla
    const toFilterProps: string[] = this.columnsData
      .filter(
        (cd) =>
          !cd.noVisibleInTable &&
          (cd.pathData?.type === 'text' || cd.pathData?.type === 'number')
      )
      .map((cd) => cd.pathData!.name);

    // Obtener todas las propiedades anidadas y que se muestren en la tabla
    const nestedProps: TableColumn[] = this.columnsData.filter(
      (cd) =>
        !cd.noVisibleInTable &&
        (cd.pathData?.type === 'select' ||
          cd.pathData?.type === 'multi-select' ||
          cd.pathData?.type === 'avatar-select')
    );

    // Solo filtrar si hay valor de búsqueda y propiedades
    if (
      this.searchValue?.trim() &&
      (toFilterProps.length > 0 || nestedProps.length > 0)
    ) {
      const searchTerm = this.searchValue.toLowerCase().trim();

      tempFilteredData = tempFilteredData.filter(
        (item) =>
          // Verificar en todas las propiedades de texto
          toFilterProps.some((prop) => {
            const value = this.getPropertyValue(item, prop);
            return value?.toString().toLowerCase().includes(searchTerm);
          }) ||
          // Verificar en todas las propiedades anidadas
          nestedProps.some((prop) => {
            const currentObj = (prop.optionList || []).find(
              (opt) => opt.id === item[prop.pathData!.name]
            );
            const value = this.getPropertyValue(
              currentObj,
              prop.pathData!.nestedPath!
            );
            return value?.toString().toLowerCase().includes(searchTerm);
          })
      );
    }

    // Aplicar filtros de campo específicos (nuevo - múltiples filtros)
    if (this.enableFilters && this.activeFilters.length > 0) {
      tempFilteredData = tempFilteredData.filter((item) => {
        // Todos los filtros deben cumplirse (AND)
        return this.activeFilters.every(activeFilter => {
          return this.applyFieldFilter(item, activeFilter.column, activeFilter.filterValue);
        });
      });
    }

    // Reaplicar el ordenamiento activo: filter() reconstruye los datos desde
    // tableData, por lo que sin esto se perderia el orden en cada cambio de
    // datos (eliminar, crear o editar), busqueda o filtro.
    this.filteredData = this.applyActiveSort(tempFilteredData);
    this.reconcileSelection();
    this.refreshRenderedData();
  }

  private applyActiveSort(rows: any[]): any[] {
    const column = this.activeSortColumn;
    const direction = column?.sortDirection ?? null;

    if (!column || !direction) {
      return rows;
    }

    return sortData(rows, { column, direction });
  }

  /**
   * Descarta de la seleccion los ids de filas que ya no existen. Se compara
   * contra tableData y no contra filteredData para que una busqueda o un filtro
   * no borren la seleccion: solo la pierden las filas realmente eliminadas.
   */
  private reconcileSelection(): void {
    if (!this.showCheckboxes || this.selectedIds.length === 0) {
      return;
    }

    const availableIds = new Set(this.tableData.map((row) => row?.id));
    this.selectedIds = this.selectedIds.filter((id) => availableIds.has(id));
    this.updateSelectionState();
  }

  private refreshRenderedData(): void {
    if (!this.enableGrouping || this.activeGroupColumns.length === 0) {
      this.renderData = [...this.filteredData];
      this.clampPageIndex();
      return;
    }

    const groupedSource = this.filterRowsByActiveGroupSelections(this.filteredData);
    const groupedRows = this.buildGroupedRows(groupedSource, this.activeGroupColumns);
    this.renderData = this.flattenGroupedRows(groupedRows);
    this.clampPageIndex();
  }

  private filterRowsByActiveGroupSelections(rows: any[]): any[] {
    if (this.activeGroupSelections.size === 0) {
      return rows;
    }

    return rows.filter((row) =>
      this.activeGroupColumns.every((column) => {
        const selectedValues = this.activeGroupSelections.get(column.name);

        if (!selectedValues || selectedValues.size === 0) {
          return true;
        }

        const valueKey = this.getGroupValueKey(
          this.getGroupColumnFilterValue(row, column),
          this.getGroupColumnDisplayValue(row, column)
        );
        return selectedValues.has(valueKey);
      })
    );
  }

  private buildGroupedRows(
    rows: any[],
    columns: TableColumn[],
    level: number = 0,
    parentKey: string = 'maestro'
  ): any[] {
    const column = columns[level];
    if (!column) {
      return rows;
    }

    const groupMap = new Map<string, { label: string; items: any[] }>();

    rows.forEach((row) => {
      const label = this.getGroupColumnDisplayValue(row, column) || 'Sin valor';
      const key = this.getGroupValueKey(
        this.getGroupColumnFilterValue(row, column),
        label
      );

      if (!groupMap.has(key)) {
        groupMap.set(key, { label, items: [] });
      }

      groupMap.get(key)!.items.push(row);
    });

    return Array.from(groupMap.values()).map((group) => {
      const groupKey = `${parentKey}-${level}-${column.pathData?.name || column.name}-${this.toGroupKey(group.label)}`;

      if (!this.initializedGroupKeys.has(groupKey)) {
        this.initializedGroupKeys.add(groupKey);
        this.expandedGroupKeys.add(groupKey);
      }

      return {
        id: groupKey,
        __groupRow: true,
        __groupLevel: level,
        groupName: group.label,
        groupColumnName: column.name,
        count: group.items.length,
        items: this.buildGroupedRows(group.items, columns, level + 1, groupKey),
      };
    });
  }

  private flattenGroupedRows(rows: any[]): any[] {
    const renderedRows: any[] = [];

    rows.forEach((row) => {
      renderedRows.push(row);

      if (row.__groupRow && this.expandedGroupKeys.has(row.id)) {
        renderedRows.push(...this.flattenGroupedRows(row.items || []));
      }
    });

    return renderedRows;
  }

  toggleGroupRow(row: any, event?: Event): void {
    event?.stopPropagation();
    if (!row?.__groupRow) return;

    if (this.expandedGroupKeys.has(row.id)) {
      this.expandedGroupKeys.delete(row.id);
    } else {
      this.expandedGroupKeys.add(row.id);
    }

    this.refreshRenderedData();
  }

  isGroupExpanded(row: any): boolean {
    return !!row?.__groupRow && this.expandedGroupKeys.has(row.id);
  }

  getGroupIndent(row: any): number {
    return Math.max(0, Number(row?.__groupLevel || 0)) * 22;
  }

  getTableColspan(): number {
    return this.columnsDataToShow.length + 1 + (this.showCheckboxes ? 1 : 0);
  }

  getColumnDisplayValue(item: any, column: TableColumn): string {
    const value = column.pathData?.displayFn?.(item);
    return value || '-';
  }

  private getGroupColumnDisplayValue(item: any, column: TableColumn): string {
    const pathData = column.pathData;
    if (!pathData) return 'Sin valor';

    if (pathData.displayFn) {
      return pathData.displayFn(item) || 'Sin valor';
    }

    if (pathData.type === 'boolean' || column.checkboxOnTable || pathData.type === 'checkboxOnTable') {
      return deepGet(item, pathData.name) ? 'Sí' : 'No';
    }

    if (pathData.type === 'select') {
      const optionName = column.optionList?.length
        ? this.findOptionNameById(item[pathData.name], column.optionList)
        : '-';

      if (optionName && optionName !== '-') {
        return optionName;
      }

      return this.getNestedDisplayName(item, pathData.name, pathData.nestedPath);
    }

    if (pathData.type === 'avatar-select') {
      const optionName = column.optionList?.length
        ? this.findOptionNameById(item[pathData.name], column.optionList)
        : '-';

      if (optionName && optionName !== '-') {
        return optionName;
      }

      return this.getNestedDisplayName(item, pathData.name, pathData.nestedPath);
    }

    const value = pathData.nestedPath
      ? deepGet(item, `${pathData.name}.${pathData.nestedPath}`)
      : deepGet(item, pathData.name);

    if (pathData.type === 'date' && value) {
      const date = new Date(value);
      return Number.isNaN(date.getTime())
        ? 'Sin valor'
        : date.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
    }

    if (value === null || value === undefined || value === '') {
      return 'Sin valor';
    }

    return String(value);
  }

  private getGroupColumnFilterValue(
    item: any,
    column: TableColumn
  ): string | number | boolean | null {
    const pathData = column.pathData;
    if (!pathData?.name) return null;

    const value = deepGet(item, pathData.name);

    if (pathData.type === 'boolean' || column.checkboxOnTable || pathData.type === 'checkboxOnTable') {
      return !!value;
    }

    if (value === undefined || value === '') {
      return null;
    }

    if (value === null || typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      return value;
    }

    return String(value);
  }

  private normalizeText(value: string): string {
    return (value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();
  }

  private toGroupKey(value: string): string {
    return this.normalizeText(value)
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'sin-valor';
  }

  findOptionNameById(id: number, list: any[]): string {
    const selected = list.find((item) => item.id === id);

    if (selected?.name || selected?.surName) {
      return getPartnerDisplayName(selected) || '-';
    }

    return selected?.role || '-';
  }

  openViewImageModal(item: any) {
    this.tmpImage = item;
    this.viewImageModalRef = this.modalService.create({
      nzTitle: undefined,
      nzClosable: false,
      nzFooter: null,
      nzContent: this.viewImageModalTmpl,
      nzCentered: true,
    });
  }

  openDetailsDrawer(item: any): void {
    if (this.isFlow) {
      combineLatest({
        isMobile: this.screenService.isMobile$,
        isMedium: this.screenService.isMedium$,
      })
        .pipe(take(1))
        .subscribe(({ isMobile, isMedium }) => {
          this.formDrawerRef = this.drawerService.create({
            nzWidth: 793,
            nzHeight: '98%',
            nzPlacement: isMobile ? 'bottom' : 'right',
            nzClosable: false,
            nzMask: false,
            nzMaskClosable: false,
            nzContent: FlowComponent,
            nzData: {
              flowId: item?.id || undefined,
              readonly: true,
            },
          });
        });
    } else {
      combineLatest({
        isMobile: this.screenService.isMobile$,
        isMedium: this.screenService.isMedium$,
      })
        .pipe(take(1))
        .subscribe(({ isMobile, isMedium }) => {
          // Open the details drawer
          this.detailsDrawerRef = this.drawerService.create({
            nzClosable: false,
            nzMask: false,
            nzWidth: this.customDrawer?.detailsComponentWidth || '500px',
            nzHeight: '98%',
            nzPlacement: isMobile ? 'bottom' : 'right',
            nzContent:
              this.customDrawer?.detailsComponent ||
              MaestrosDetailsDrawerComponent,
            nzData: {
              columnsData: this.columnsData,
              element: item,
              maestroName: this.maestroName,
              editPermissions: this.permissions.edit,
              disableNavigation:
                this.customDrawer?.detailsComponent ===
                  OrdersDetailsActivitiesDrawerComponent &&
                this.maestroName === 'Subtareas',
            },
          });

          // Handle drawer open and close events
          this.detailsDrawerRef.afterOpen.pipe(take(1)).subscribe(() => {
            this.ms.detailsDrawerRef = this.detailsDrawerRef;

            this.detailsDrawerRef
              .getContentComponent()
              .openEditDrawerEvent?.pipe(take(1))
              .subscribe((id: number) => {
                const selectedElement = this.tableData.find((i) => i.id === id);
                this.detailsDrawerRef.close();
                this.openFormDrawer(selectedElement);
              });

            // Handle service order specific events
            if (
              this.customDrawer?.detailsComponent ===
                OrdersDetailsActivitiesDrawerComponent &&
              this.maestroName === 'Subtareas'
            ) {
              const contentComponent =
                this.detailsDrawerRef.getContentComponent();

              // Limpiar suscripciones anteriores
              this.clearDrawerSubscriptions();

              // Handle delete service order event - show confirmation modal
              const deleteSub =
                contentComponent.deleteServiceOrderEvent?.subscribe(
                  (id: number) => {
                    this.pendingDeleteId = id;
                    this.openDeleteConfirmation(id);
                  }
                );
              if (deleteSub) this.drawerSubscriptions.push(deleteSub);

              // Handle create subtask event - open form drawer for new subtask
              const createSub = contentComponent.createSubtaskEvent?.subscribe(
                () => {
                  // No cerrar el details drawer - permanecer en la misma página
                  this.openFormDrawer(); // Open form drawer for new subtask
                }
              );
              if (createSub) this.drawerSubscriptions.push(createSub);
            }
          });

          this.detailsDrawerRef.afterClose.pipe(take(1)).subscribe(() => {
            this.ms.detailsDrawerRef = undefined;
            // Limpiar suscripciones cuando se cierra el drawer
            this.clearDrawerSubscriptions();
          });
        });
    }
  }

  emitSecondaryBtnClick() {
    this.secondaryBtnClick.emit();
  }

  ngOnDestroy() {
    this.formDrawerRef?.close();
    this.detailsDrawerRef?.close();
    this.groupDrawerRef?.close();
    this.clearDrawerSubscriptions();
  }

  // Función auxiliar para obtener valores de propiedades anidadas
  private getPropertyValue(obj: any, path: string): any {
    return path ? path.split('.').reduce((acc, part) => acc?.[part], obj) : '';
  }

  protected readonly isExpirationAlert = isExpirationAlert;

  getDocumentTypeName(documentTypeId: number): string | undefined {
    return (this.gs.documentTypes$?.value || []).find(
      (dt) => dt.id === documentTypeId
    )?.name;
  }

  protected readonly console = console;
  protected readonly deepGet = deepGet;

  setCurrencyData(
    rate: number,
    currencyIso: string,
    isReverse: boolean
  ): { firstIso: string; rate: number; lastIso: string } {
    if (isReverse) {
      return {
        firstIso: currencyIso,
        rate: 1 / rate,
        lastIso: 'USD',
      };
    } else {
      return {
        firstIso: 'USD',
        rate: rate,
        lastIso: currencyIso,
      };
    }
  }

  // Métodos para el sistema de ordenamiento
  private configureSortFunctions(): void {
    SortHelper.configureSortFunctions(this.columnsDataToShow, (column) =>
      this.handleSort(column)
    );
  }

  // Registra el orden por defecto como orden activo: filter() lo reaplica en cada cambio de
  // datos, búsqueda o filtro, igual que un orden elegido pulsando la cabecera.
  private applyDefaultSort(): void {
    const column = SortHelper.applyDefaultSort(this.columnsDataToShow, this.defaultSort);
    if (!column) return;

    this.activeSortColumn = column;
    this.filter();
  }

  handleSort(column: TableColumn): void {
    if (!column.pathData) return;

    const newDirection = getNextSortDirection(column.sortDirection || null);

    // Resetear otras columnas y actualizar la actual
    SortHelper.resetOtherColumns(this.columnsDataToShow, column);
    column.sortDirection = newDirection;
    this.activeSortColumn = newDirection ? column : undefined;

    // filter() se encarga de reaplicar busqueda, filtros y el orden recien
    // registrado, de modo que exista un unico camino de renderizado.
    this.filter();
  }

  // Método para obtener la dirección de ordenamiento de una columna
  getColumnSortDirection(column: TableColumn): SortDirection {
    return SortHelper.getColumnSortDirection(column);
  }

  updateSelectionState(): void {
    const totalCount = this.filteredData.length;
    const selectedCount = this.selectedIds.length;

    this.allSelected = totalCount > 0 && selectedCount === totalCount;
    this.indeterminate = selectedCount > 0 && selectedCount < totalCount;
  }

  onSelectAll(checked: boolean): void {
    if (checked) {
      this.selectedIds = this.filteredData.map((item) => item.id);
    } else {
      this.selectedIds = [];
    }
    this.updateSelectionState();

    // Emitir evento al componente padre si es necesario
    // this.selectionChange.emit(this.selectedIds);
  }

  getColumnClass(
    columnName: string,
    sortable: boolean = false,
    isGrey: boolean = false
  ): string {
    const baseClass =
      'column-' +
      columnName
        .toLowerCase()
        .replace(/ /g, '-')
        .replace(/[^\w-]/g, '');
    const sortableClass = sortable ? ' sorteable-column' : '';
    const greyClass = isGrey ? ' color-grey-9' : '';
    return baseClass + sortableClass + greyClass;
  }

  onSelectItem(id: number, checked: boolean): void {
    if (checked) {
      this.selectedIds.push(id);
    } else {
      this.selectedIds = this.selectedIds.filter(
        (selectedId) => selectedId !== id
      );
    }
    this.updateSelectionState();

    // Emitir evento al componente padre si es necesario
    // this.selectionChange.emit(this.selectedIds);
  }

  updateGroupValuesSelectionState(): void {
    if (!this.shownGroupValueOptions.length) {
      this.allGroupValuesSelected = false;
      this.groupValuesIndeterminate = false;
      return;
    }

    const totalAll = this.selectedGroupValueOptions.length;
    const selectedAll = this.selectedGroupValueOptions.filter((o) => o.isActive).length;

    this.allGroupValuesSelected = totalAll > 0 && selectedAll === totalAll;
    this.groupValuesIndeterminate = selectedAll > 0 && selectedAll < totalAll;
  }

  toggleSelectAllGroupValues(checked: boolean): void {
    if (!this.selectedGroupColumn) return;

    const columnName = this.selectedGroupColumn.name;
    const selectedValues = this.getDraftSelectionSet(columnName);

    this.shownGroupValueOptions.forEach((option) => {
      option.isActive = checked;
      if (checked) {
        selectedValues.add(option.key);
      } else {
        selectedValues.delete(option.key);
      }
    });

    this.selectedGroupValueOptions = this.selectedGroupValueOptions.map((item) => {
      const shown = this.shownGroupValueOptions.find((o) => o.key === item.key);
      if (shown) {
        return { ...item, isActive: checked };
      }
      return item;
    });

    if (!checked && selectedValues.size === 0) {
      this.draftGroupSelections.delete(columnName);
      this.draftGroupColumns = this.draftGroupColumns.filter(
        (c) => c.name !== columnName,
      );
    } else if (
      checked &&
      !this.draftGroupColumns.some((c) => c.name === columnName)
    ) {
      this.draftGroupColumns = [
        ...this.draftGroupColumns,
        this.selectedGroupColumn,
      ];
    }

    this.updateGroupValuesSelectionState();
  }

  private clearDrawerSubscriptions(): void {
    this.drawerSubscriptions.forEach((sub) => sub.unsubscribe());
    this.drawerSubscriptions = [];
  }
}
